import {
  StatusProxy,
  Tag,
  ToolBarItemType,
  type ToolBarItem,
} from "@focus/focusUI";
import { useState } from "react";
import { useMe } from "../../hooks/useMe";
import { ROLES, useRoles } from "../../hooks/useRoles";
import { useUserController } from "../../hooks/useUserController";
import type { User } from "../../types/users";
import { Table } from "../table/Table";
import { TableShell } from "../table/TableShell";
import { CreateOrganizationButton } from "./CreateOrganizationButton";
import { CreateUserButton } from "./CreateUserButton";
import { DeleteUserButton } from "./DeleteUserButton";
import { EditUserButton } from "./EditUserButton";
import { EnableDisableButton } from "./EnableDisableButton";
import { ResetPasswordButton } from "./ResetPasswordButton";
import { RestoreTotpButton } from "./RestoreTotpButton";

export const UserAccessTable = () => {
  const me = useMe();
  const { roles } = useRoles();
  const { users, getRequestStatus, getRequestError, organizations } =
    useUserController();

  const [rowSelectionState, setRowSelectionState] = useState<
    Record<string, boolean>
  >({});

  const selectedUserId = Object.keys(rowSelectionState).find(
    (key) => rowSelectionState[key],
  );
  const selectedUser = users.find((user) => user.id === selectedUserId);

  const isSelf = me && selectedUser && selectedUser.id === me.id;

  const columnDefs = [
    {
      headerName: "Organization",
      field: "organization",
      key: "organization",
      minSize: 260,
      size: 300,
    },
    {
      headerName: "Username",
      field: "username",
      key: "username",
      hide: false,
      minSize: 180,
      size: 220,
    },
    {
      headerName: "Firstname",
      field: "firstName",
      key: "firstName",
      hide: false,
      minSize: 160,
      size: 180,
    },
    {
      headerName: "Lastname",
      field: "lastName",
      key: "lastName",
      hide: false,
      minSize: 160,
      size: 180,
    },
    {
      headerName: "Email",
      field: "email",
      key: "email",
      hide: false,
      minSize: 240,
      size: 300,
    },
    {
      headerName: "Role Groups",
      key: "roleGroups",
      minSize: 220,
      size: 260,
      customCellRenderer: ({ row, getValue }) => {
        if (row.depth === 0) return;
        const value = getValue() as string[] | undefined;
        const roles = value ?? [];
        if (!roles.length) return "-";

        return (
          <div className="flex flex-wrap gap-1 !p-2">
            {roles.map((r: string, i: number) => (
              <Tag key={i} round minimal>
                {r}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      headerName: "User state",
      field: "enabled",
      key: "enabled",
      minSize: 140,
      size: 150,
      customValueGetter: ({ row, key }) => {
        if (row.depth === 0) return null;
        return row[key] === true ? "Enabled" : "Disabled";
      },
    },
  ];
  const serverDisplayConfig = columnDefs.reduce(
    (acc, c, i) => ({
      ...acc,
      [c.key]: { visible: true, order: i, width: c.size ?? c.minSize ?? 180 },
    }),
    {},
  );

  function groupByNameAsArray(items: User[], organizations: string[]) {
    const map = new Map<string, User[]>(organizations.map((org) => [org, []]));

    for (const item of items) {
      if (!item.organizations || item.organizations.length === 0) {
        continue;
      }

      for (const organizationName of item.organizations) {
        if (!map.has(organizationName) && roles.includes(ROLES.ADMIN)) {
          map.set(organizationName, []);
        }
        if (map.has(organizationName)) {
          map.get(organizationName)!.push(item);
        }
      }
    }

    return Array.from(map.entries()).map(([organizationName, items]) => ({
      organization: organizationName,
      subRows: items,
      depth: 0,
    }));
  }

  const status = getRequestStatus(["getUsers", "getUserMe"]);
  const error = getRequestError(["getUsers", "getUserMe"]);

  const leftMenuItems: ToolBarItem<ToolBarItemType.Custom>[] = [
    {
      type: ToolBarItemType.Custom,
      component: (
        <EditUserButton
          user={selectedUser}
          disabled={!selectedUser}
          isSelf={isSelf}
        />
      ),
    },
    {
      type: ToolBarItemType.Custom,
      component: (
        <ResetPasswordButton user={selectedUser} disabled={!selectedUser} />
      ),
    },
    {
      type: ToolBarItemType.Custom,
      component: (
        <RestoreTotpButton user={selectedUser} disabled={!selectedUser} />
      ),
    },
    {
      type: ToolBarItemType.Custom,
      component: (
        <EnableDisableButton
          user={selectedUser}
          disabled={!selectedUser || isSelf}
        />
      ),
    },
    {
      type: ToolBarItemType.Custom,
      component: (
        <DeleteUserButton
          user={selectedUser}
          disabled={!selectedUser || isSelf}
        />
      ),
    },
    {
      type: ToolBarItemType.Custom,
      component: <CreateUserButton />,
    },
    {
      type: ToolBarItemType.Custom,
      component: <CreateOrganizationButton />,
    },
  ];

  return (
    <div className="h-full min-h-0">
      <StatusProxy status={status} error={error}>
        <TableShell className="user-management-table-shell">
          <Table
            columnDefinitions={columnDefs}
            serverDisplayConfig={serverDisplayConfig}
            defaultVisibleValues={columnDefs.map((c) => c.key)}
            rowData={groupByNameAsArray(users, organizations)}
            base={"user-access"}
            height="auto"
            showGlobalFilter
            rowClassRules={{
              "!bg-slate-50": (row) => row.depth === 0,
              "!font-semibold": (row) => row.depth === 0,
            }}
            controlledRowSelection={rowSelectionState}
            controlledRowSelectionChange={setRowSelectionState}
            enableMultiSelection={false}
            leftMenuItems={leftMenuItems}
          />
        </TableShell>
      </StatusProxy>
    </div>
  );
};
